(() => {
  if (customElements.get('wolf-pdp-content')) return;

  class WolfPdpContent extends HTMLElement {
    connectedCallback() {
      this.events?.abort();
      this.events = new AbortController();
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.disclosures = [...this.querySelectorAll('.wolf-pdp-content__disclosure')].map((details) => {
        const summary = details.querySelector('summary');
        const state = { details, summary, content: summary.nextElementSibling, expanded: details.open, animation: null };
        this.updateSemantics(state);
        summary.addEventListener('click', (event) => {
          if (event.button > 0) return;
          event.preventDefault();
          this.setExpanded(state, !state.expanded);
        }, { signal: this.events.signal });
        details.addEventListener('keyup', (event) => {
          if (event.key !== 'Escape' || !state.expanded) return;
          event.preventDefault();
          event.stopPropagation();
          this.setExpanded(state, false);
          summary.focus({ preventScroll: true });
        }, { signal: this.events.signal });
        details.addEventListener('toggle', () => {
          if (state.animation) return;
          state.expanded = details.open;
          this.updateSemantics(state);
        }, { signal: this.events.signal });
        return state;
      });

      this.reducedMotion.addEventListener('change', () => {
        if (this.reducedMotion.matches) this.disclosures.forEach((state) => this.settle(state));
      }, { signal: this.events.signal });

      this.updateSliderWidth();
      window.addEventListener('resize', () => this.updateSliderWidth(), { signal: this.events.signal });

      // Images, font loading and viewport changes can alter the natural target.
      // Only retarget while opening; settled content always has automatic height.
      if (window.ResizeObserver) {
        this.resizeObserver?.disconnect();
        this.resizeObserver = new ResizeObserver((entries) => {
          this.updateSliderWidth();
          this.disclosures.forEach((state) => {
            if (state.animation && state.expanded && entries.some((entry) => entry.target === state.content)) {
              this.setExpanded(state, true);
            }
          });
        });
        this.disclosures.forEach((state) => this.resizeObserver.observe(state.content));
        this.resizeObserver.observe(this);
      }
    }

    updateSliderWidth() {
      const bounds = this.getBoundingClientRect();
      const viewportWidth = document.documentElement.clientWidth;
      if (!bounds.width || !viewportWidth) return;
      const contentWidth = `${bounds.width}px`;
      const detailsWidth = `${Math.max(0, viewportWidth - bounds.left)}px`;
      const media = this.closest('.product')?.querySelector('.product__media-wrapper');
      const mediaBounds = media?.getBoundingClientRect();
      // Extend across the desktop column gap and slightly behind the photo.
      // Mobile reaches the viewport edge, with the original gutter inside the scroller.
      const besideMedia = viewportWidth >= 990 && mediaBounds?.width > 0 && mediaBounds.right <= bounds.left;
      const bleed = viewportWidth < 750 ? bounds.left
        : besideMedia ? Math.min(bounds.left, bounds.left - mediaBounds.right + 24) : 0;
      const detailsBleed = `${Math.max(0, bleed)}px`;
      const endInset = `${Math.max(0, viewportWidth - bounds.left - bounds.width)}px`;
      // Keep the heading in its column, but include the viewport gutter inside
      // the animated disclosure so its cards never clip at the column edge.
      if (this.style.getPropertyValue('--wolf-content-width') !== contentWidth) this.style.setProperty('--wolf-content-width', contentWidth);
      if (this.style.getPropertyValue('--wolf-details-width') !== detailsWidth) this.style.setProperty('--wolf-details-width', detailsWidth);
      if (this.style.getPropertyValue('--wolf-details-bleed') !== detailsBleed) this.style.setProperty('--wolf-details-bleed', detailsBleed);
      if (this.style.getPropertyValue('--wolf-details-end-inset') !== endInset) this.style.setProperty('--wolf-details-end-inset', endInset);
    }

    disconnectedCallback() {
      this.events?.abort();
      this.resizeObserver?.disconnect();
      this.disclosures?.forEach((state) => this.settle(state));
    }

    updateSemantics(state) {
      state.summary.setAttribute('aria-expanded', String(state.expanded));
      state.details.dataset.wolfExpanded = String(state.expanded);
      state.content.inert = !state.expanded;
    }

    settle(state) {
      const animation = state.animation;
      state.animation = null;
      if (animation) {
        animation.onfinish = null;
        animation.cancel();
      }
      state.details.open = state.expanded;
      state.details.style.height = '';
      state.details.style.overflow = '';
      this.updateSemantics(state);
    }

    setExpanded(state, expanded) {
      const { details, summary } = state;
      this.updateSliderWidth();
      const startHeight = details.getBoundingClientRect().height;
      if (state.animation) {
        state.animation.onfinish = null;
        state.animation.cancel();
        state.animation = null;
      }
      state.expanded = expanded;
      this.updateSemantics(state);

      if (this.reducedMotion.matches || typeof details.animate !== 'function') {
        this.settle(state);
        return;
      }

      // Keep native content rendered through the closing animation. Reversals
      // begin at the current visual height rather than snapping to an endpoint.
      details.open = true;
      details.style.height = '';
      const endHeight = expanded ? details.getBoundingClientRect().height : summary.getBoundingClientRect().height;
      if (Math.abs(startHeight - endHeight) < 1) {
        this.settle(state);
        return;
      }
      details.style.height = `${startHeight}px`;
      details.style.overflow = 'hidden';
      const animation = details.animate(
        [{ height: `${startHeight}px` }, { height: `${endHeight}px` }],
        { duration: 340, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'forwards' }
      );
      state.animation = animation;
      animation.onfinish = () => {
        if (state.animation === animation) this.settle(state);
      };
    }
  }

  customElements.define('wolf-pdp-content', WolfPdpContent);
})();
